import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { ActivityIndicator, KeyboardAvoidingView, Platform, Pressable, ScrollView, useColorScheme } from 'react-native';
import { ImagePlus } from 'lucide-react-native';
import { Label, Paragraph, View, XStack, YStack } from 'tamagui';

import { Heading } from '@/components/heading';
import { Input } from '@/components/input';
import { LocationRadiusPicker } from '@/components/location-radius-picker';
import { PrimaryButton } from '@/components/primary-button';
import { ScreenContainer } from '@/components/screen-container';
import { SolidSurface } from '@/components/glass-surface';
import { Text } from '@/components/text';
import { TextArea } from '@/components/text-area';
import { TextLink } from '@/components/text-link';
import { useCurrentLocation } from '@/hooks/use-current-location';
import { useMyProfile } from '@/hooks/use-my-profile';
import { useProfileSave } from '@/hooks/use-profile-save';
import { api } from '@/lib/api';
import { HANDLE_POLICY_COPY, MAX_PROMPT_ANSWER_LEN, profileHandleValid } from '@/lib/content-limits';
import { errorMessage } from '@/lib/photo-errors';
import { pickShrunkPhoto } from '@/lib/pick-photo';
import { PROMPT_BANK, shuffle } from '@/lib/prompt-bank';
import { queryKeys } from '@/lib/query-keys';
import { borderWidth, darkColors, lightColors, space } from '@/theme/tokens.generated';

const STEPS = 3;
const DEFAULT_RADIUS_M = 5000;

// Issue #244/#238: first-run as three short screens instead of one long
// form. Every step is a real save through the same mutations the edit
// screens use, so quitting after any step leaves a valid profile (the
// profile exists from step 1 on) and nothing here is wizard-only state
// the backend has to know about.
export function FirstRunWizard({
  handle,
  onHandleChange,
  onRegenerate,
  regenerating,
}: {
  handle: string;
  onHandleChange: (value: string) => void;
  onRegenerate: () => void;
  regenerating: boolean;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);

  const finish = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.threads() });
    queryClient.invalidateQueries({ queryKey: queryKeys.me() });
    router.replace('/');
  };

  return (
    <ScreenContainer>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <YStack gap="$2" pt="$4" pb="$4">
          <XStack gap="$2" accessibilityRole="progressbar" aria-valuemin={1} aria-valuemax={STEPS} aria-valuenow={step}>
            {Array.from({ length: STEPS }, (_, i) => (
              <View
                key={i}
                flex={1}
                height={4}
                borderRadius="$full"
                backgroundColor={i < step ? '$colorLink' : '$borderColor'}
              />
            ))}
          </XStack>
          <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
            Step {step} of {STEPS}
          </Text>
        </YStack>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingBottom: space[6], gap: space[5] }}
          keyboardShouldPersistTaps="handled"
        >
          {step === 1 && (
            <HandleStep
              handle={handle}
              onHandleChange={onHandleChange}
              onRegenerate={onRegenerate}
              regenerating={regenerating}
              onDone={() => setStep(2)}
            />
          )}
          {step === 2 && <PromptStep onDone={() => setStep(3)} />}
          {step === 3 && <PhotoStep onDone={finish} />}
        </ScrollView>
      </KeyboardAvoidingView>
    </ScreenContainer>
  );
}

function HandleStep({
  handle,
  onHandleChange,
  onRegenerate,
  regenerating,
  onDone,
}: {
  handle: string;
  onHandleChange: (value: string) => void;
  onRegenerate: () => void;
  regenerating: boolean;
  onDone: () => void;
}) {
  const [radiusM, setRadiusM] = useState(DEFAULT_RADIUS_M);
  const location = useCurrentLocation();
  const save = useProfileSave({ onSuccess: onDone });
  const valid = profileHandleValid(handle.trim());
  const canContinue = valid && location.phase.phase === 'granted';

  return (
    <YStack gap="$6">
      <YStack gap="$2">
        <Heading>Pick a handle and your area</Heading>
        <Paragraph color="$colorSecondary">Neighbors see your handle, never your name or exact location.</Paragraph>
      </YStack>

      <YStack gap="$1">
        <XStack alignItems="center" justifyContent="space-between">
          <Label htmlFor="wizard-handle" color="$colorSecondary">
            Handle
          </Label>
          <TextLink fontSize="$meta" lineHeight="$meta" onPress={() => !regenerating && onRegenerate()}>
            {regenerating ? 'Generating...' : 'Regenerate'}
          </TextLink>
        </XStack>
        <Input
          id="wizard-handle"
          value={handle}
          onChangeText={onHandleChange}
          autoCapitalize="none"
          autoCorrect={false}
          textContentType="username"
        />
        {!valid && handle.trim() !== '' && (
          <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta">
            Handles can use {HANDLE_POLICY_COPY.toLowerCase()}
          </Paragraph>
        )}
      </YStack>

      <LocationRadiusPicker location={location} radiusM={radiusM} onRadiusChange={setRadiusM} />

      {save.isError && (
        <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta" role="alert">
          {errorMessage(save.error)}
        </Paragraph>
      )}

      <PrimaryButton
        disabled={!canContinue}
        loading={save.isPending}
        onPress={() => {
          if (save.isPending || location.phase.phase !== 'granted' || !valid) return;
          save.mutate({
            handle: handle.trim(),
            lat: location.phase.coords.lat,
            lng: location.phase.coords.lng,
            radiusM,
          });
        }}
      >
        Continue
      </PrimaryButton>
    </YStack>
  );
}

function PromptStep({ onDone }: { onDone: () => void }) {
  // Freeform only: a single short answer is the whole step.
  const options = useMemo(() => shuffle(PROMPT_BANK.filter((entry) => entry.type === 'freeform')).slice(0, 3), []);
  const [question, setQuestion] = useState<string | null>(null);
  const [answer, setAnswer] = useState('');
  const save = useProfileSave({ onSuccess: onDone });

  return (
    <YStack gap="$6">
      <YStack gap="$2">
        <Heading>Say something about yourself</Heading>
        <Paragraph color="$colorSecondary">Pick one. It is the first thing a neighbor reads.</Paragraph>
      </YStack>

      <YStack gap="$2" accessibilityRole="radiogroup">
        {options.map((entry) => {
          const selected = entry.question === question;
          return (
            <Pressable
              key={entry.question}
              accessibilityRole="radio"
              aria-checked={selected}
              onPress={() => setQuestion(entry.question)}
            >
              <SolidSurface
                style={{ padding: space[4], borderWidth: selected ? borderWidth.control : borderWidth.hairline }}
              >
                <Text color={selected ? '$color' : '$colorLink'} fontWeight={selected ? '600' : '400'}>
                  {entry.question}
                </Text>
              </SolidSurface>
            </Pressable>
          );
        })}
      </YStack>

      {question && (
        <YStack gap="$1">
          <Label htmlFor="wizard-answer" color="$colorSecondary">
            Your answer
          </Label>
          <TextArea
            id="wizard-answer"
            value={answer}
            onChangeText={setAnswer}
            maxLength={MAX_PROMPT_ANSWER_LEN}
            numberOfLines={3}
          />
        </YStack>
      )}

      {save.isError && (
        <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta" role="alert">
          {errorMessage(save.error)}
        </Paragraph>
      )}

      <YStack gap="$3" alignItems="center">
        <PrimaryButton
          disabled={!question || answer.trim() === ''}
          loading={save.isPending}
          onPress={() => question && save.mutate({ prompts: [{ question, answer: answer.trim() }] })}
        >
          Continue
        </PrimaryButton>
        <TextLink onPress={onDone}>Skip for now</TextLink>
      </YStack>
    </YStack>
  );
}

function PhotoStep({ onDone }: { onDone: () => void }) {
  // Full-replace PATCH from current server state (useProfileSave), so the
  // photo save cannot wipe the handle/prompt saved in earlier steps.
  const profile = useMyProfile();
  const save = useProfileSave();
  const colorScheme = useColorScheme();
  const linkColor = colorScheme === 'dark' ? darkColors.textLink : lightColors.textLink;
  const add = useMutation({
    mutationFn: async () => {
      const photo = await pickShrunkPhoto();
      const uploaded = await api.uploadPhoto(photo.base64, photo.mimeType);

      try {
        await save.mutateAsync({ photos: [...(profile.data?.photos ?? []), uploaded.id] });
      } catch (error) {
        // An unattached blob still counts against the photo cap.
        await api.deletePhoto(uploaded.id).catch(() => {});
        throw error;
      }
    },
    onSuccess: onDone,
  });

  return (
    <YStack gap="$6">
      <YStack gap="$2">
        <Heading>Add a photo</Heading>
        <Paragraph color="$colorSecondary">
          Neighbors only see it after you both agree to reveal. You can add more later.
        </Paragraph>
      </YStack>

      {add.isError && (
        <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta" role="alert">
          {errorMessage(add.error)}
        </Paragraph>
      )}

      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Choose a photo"
        disabled={add.isPending}
        onPress={() => add.mutate()}
      >
        <SolidSurface style={{ height: 240, alignItems: 'center', justifyContent: 'center', gap: space[3] }}>
          {add.isPending ? <ActivityIndicator /> : <ImagePlus size={24} strokeWidth={1.75} color={linkColor} />}
          <Text color="$colorLink" fontWeight="$semibold">
            {add.isPending ? 'Uploading...' : 'Choose a photo'}
          </Text>
        </SolidSurface>
      </Pressable>

      <YStack alignItems="center">
        <TextLink onPress={onDone}>Skip for now</TextLink>
      </YStack>
    </YStack>
  );
}
